import { selectIsSummaryStreaming, setSelected } from '@platform/summary-ui';
import { Icon, Paper, Typography } from '@shared/ui';
import { useDispatch, useSelector } from 'react-redux';
import { registerWidgetRenderer } from '../registry';
import type { ArtifactMetaEntity, TimelineWidgetProps } from '../types';

type Props = TimelineWidgetProps<ArtifactMetaEntity> & { convId?: string };

export function ArtifactMetaWidget({ entity, convId }: Props) {
  const dispatch = useDispatch();
  const isSummaryStreaming = useSelector(selectIsSummaryStreaming(convId));

  const { title, revision, artifact_id: artifactId } = entity.props;
  const versionLabel = typeof revision === 'number' && revision > 0 ? `v${revision}` : 'v?';
  const name = typeof title === 'string' && title.trim() ? title.trim() : 'Artifact';

  const canSelect =
    !!convId &&
    !isSummaryStreaming &&
    typeof artifactId === 'string' &&
    artifactId.trim() !== '' &&
    typeof revision === 'number' &&
    revision > 0;

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      aria-disabled={!canSelect}
      onClick={() => {
        if (!canSelect) return;
        dispatch(
          setSelected({
            convId: convId!,
            selection: { artifactId: artifactId.trim(), revision, source: 'artifact_meta' },
          })
        );
      }}
      onKeyDown={(e) => {
        if (!canSelect) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        dispatch(
          setSelected({
            convId: convId!,
            selection: { artifactId: artifactId.trim(), revision, source: 'artifact_meta' },
          })
        );
      }}
      className={[
        'p-2 w-full min-w-0 bg-slate-50 border-l-4 border-l-slate-400',
        canSelect ? 'cursor-pointer' : 'cursor-default opacity-70',
      ].join(' ')}
      data-testid="artifact-meta-card"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}>description</Icon>
        <Typography variant="caption" color="text.secondary" className="flex-1 min-w-0 break-words">
          {name} · {versionLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary" className="flex-shrink-0">
          {new Date(entity.timestamp).toLocaleTimeString()}
        </Typography>
      </div>
    </Paper>
  );
}

registerWidgetRenderer(
  'artifact_meta',
  ({ entity, convId }) => (
    <ArtifactMetaWidget key={(entity as any).id} entity={entity as any} convId={convId} />
  ),
  { visibility: { normal: true, debug: true } }
);
